import React, { useEffect, useRef } from 'react';
import CalendarEventCard from './CalendarEventCard';
import { getDateHeader, groupEventsByDay, groupEventsByAccount } from './calendarEventUtils';
import './CalendarEventCards.css';

/**
 * Calendar event cards container.
 * Shows date header, groups by day (multi-day), groups by Google account (if multiple),
 * renders card grid.
 *
 * All display values (time_range, duration, day_label, user_timezone_abbr) are
 * pre-computed by the backend — NO timezone conversion on frontend.
 *
 * @param {Object} data - { events: [...], metadata: {...} }
 * @param {function} [onLoad] - Callback when cards mount (e.g. scrollToBottom)
 * @param {function} [onBookRoom] - Called with the event when the user taps
 *   the "Book Meeting Room" button on a card.
 */
const CalendarEventCards = ({ data, onLoad, onBookRoom }) => {
  const { events, metadata } = data;
  const indexRef = useRef(0);

  useEffect(() => {
    if (onLoad) {
      // Delay slightly so cards render before scroll
      const timer = setTimeout(onLoad, 100);
      return () => clearTimeout(timer);
    }
  }, [onLoad]);

  if (!events || events.length === 0) return null;

  const isMultiDay = metadata?.query_type === 'multi_day';
  const dateHeader = getDateHeader(metadata, events);

  // Check if multiple Google accounts
  const uniqueAccounts = new Set(events.map(e => e.calendar_name || 'Calendar'));
  const showAccountLabels = uniqueAccounts.size > 1;

  // Reset global index for each render
  indexRef.current = 0;

  // Render a group of events as cards (within a day or account section)
  const renderCardGrid = (eventsToRender) => {
    return (
      <div className="personal-schedule-cards-container">
        {eventsToRender.map((event) => {
          const cardIndex = indexRef.current++;
          return (
            <CalendarEventCard
              key={event.google_event_id || `cal-event-${cardIndex}`}
              event={event}
              index={cardIndex}
              onBookRoom={onBookRoom}
            />
          );
        })}
      </div>
    );
  };

  // Render events for a single group (optionally split by account)
  const renderEventsWithAccountGroups = (groupEvents) => {
    if (!showAccountLabels) {
      return renderCardGrid(groupEvents);
    }
    // Sub-group by account
    const accountGroups = groupEventsByAccount(groupEvents);
    return [...accountGroups.entries()].map(([account, accountEvents]) => (
      <div key={account}>
        <div className="calendar-account-label">{account}</div>
        {renderCardGrid(accountEvents)}
      </div>
    ));
  };

  return (
    <div className="calendar-events-container">
      {/* Top-level header: "Today, Mar 11 · Kiln Boulder" */}
      <div className="calendar-events-date-header">
        {dateHeader}{metadata?.user_location ? ` · ${metadata.user_location}` : ''}
      </div>

      {isMultiDay ? (
        // MULTI-DAY: Group by day_label, show day sub-headers
        [...groupEventsByDay(events).entries()].map(([dayLabel, dayEvents]) => (
          <div key={dayLabel} className="calendar-day-group">
            <div className="calendar-day-label">{dayLabel}</div>
            {renderEventsWithAccountGroups(dayEvents)}
          </div>
        ))
      ) : (
        // SINGLE-DAY: No day sub-headers, just account groups
        renderEventsWithAccountGroups(events)
      )}
    </div>
  );
};

export default React.memo(CalendarEventCards);
